import { Z_INDEX } from '@va/constants';
import { getAccountInformation } from '@va/dashboard/selectors/api';
import { getCurrentPlanName } from '@va/dashboard/selectors/app';
import { getWebsite, websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { getChatwootToken } from '@va/dashboard/selectors/customization';
import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { InternalChatwootEvents } from '@va/dashboard/util-helpers/chatwoot';
import { ChatIcon, LoaderIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { Button, ButtonShapes } from '@va/ui/design-system';
import { sleep } from '@va/util/helpers';
import { EventBus } from '@va/util/misc';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import './chatwoot_widget.css';
import { ChatwootHelpers } from './helpers';

const ChatwootSDKEvents = {
  CHATWOOT_READY: 'chatwoot:ready',
  CHATWOOT_ERROR: 'chatwoot:error',
  CHATWOOT_ON_MESSAGE: 'chatwoot:on-message',
  CHATWOOT_OPENED: 'chatwoot:opened',
  CHATWOOT_CLOSED: 'chatwoot:closed',
  CHATWOOT_POSTBACK: 'chatwoot:postback',
  CHATWOOT_ON_START_CONVERSATION: 'chatwoot:on-start-conversation',
};

enum ChatwootState {
  INITIAL,
  LOADING,
  LOADED,
  FAILED,
}

const useChatwootAttributes = () => {
  const websiteInfo = useSelector(getWebsite);
  const role = useSelector(websiteRoleSelectors.selectWebsiteRole);
  const planName = useSelector(getCurrentPlanName);
  const accountInformation = useSelector(getAccountInformation);

  return {
    website_url: websiteInfo?.url,
    website_id: websiteInfo?.id,
    email: ChatwootHelpers.getEmail(accountInformation, websiteInfo),
    name: ChatwootHelpers.getName(accountInformation, websiteInfo),
    website_platform: ChatwootHelpers.getPlatform(websiteInfo),
    website_subscription: planName ?? 'none',
    website_role: role,
    website_created_at: dayjs(websiteInfo?.installDate * 1000).format('MMM D YYYY, h:mm:ss'),
    user_created_at: accountInformation?.createdDate
      ? dayjs(accountInformation?.createdDate * 1000).format('MMM D YYYY, h:mm:ss')
      : undefined,
  };
};

export const ChatwootWidget = ({ forcedLocale }: { forcedLocale?: string }) => {
  const [chatwootState, setChatwootState] = useState<ChatwootState>(ChatwootState.INITIAL);
  const chatwootToken = useSelector(getChatwootToken);
  const translate = useTranslate();
  const isMobile = useSelector(isMobileDevice);
  const attrs = useChatwootAttributes();
  const areAttributesSet = useRef(false);

  const isInvoicePage = window.location.pathname.includes('/invoice/');

  const { locale: dashboardLocale } = useLocale();

  const locale = useMemo(() => forcedLocale ?? dashboardLocale, [dashboardLocale, forcedLocale]);

  useEffect(() => {
    if (!chatwootToken) return;

    window.chatwootSettings = {
      hideMessageBubble: true,
      position: 'right',
      locale: locale,
      type: 'standard',
    };
  }, [chatwootToken, locale]);

  useEffect(() => {
    window.$chatwoot?.setLocale(locale);
  }, [locale]);

  const onLoad = useCallback(() => {
    window.$chatwoot?.toggle('open');

    setChatwootAttributes(attrs);

    setChatwootState(ChatwootState.LOADED);
    setTimeout(() => {
      setChatwootState(ChatwootState.INITIAL);
    }, 500);
  }, [attrs]);

  const onError = useCallback((err: unknown) => {
    setChatwootState(ChatwootState.FAILED);
    if (process.env.NODE_ENV === 'development') {
      console.log('Chatwoot error', err);
    }
  }, []);

  const onMessage = useCallback(async () => {
    if (!areAttributesSet.current) {
      // Making sure the attributes are set. For some reasone, right after a new conversation is initialized, the attributes request fail with 404 - perhaps the conversation is not defined yet at that stage.
      await sleep(3000);
      setChatwootAttributes(attrs);
      areAttributesSet.current = true;
    }
  }, [attrs]);

  const initializeWidget = useCallback(() => {
    if (window.$chatwoot?.hasLoaded) {
      window.$chatwoot?.toggle('open');
      return;
    }

    setChatwootState(ChatwootState.LOADING);

    (function (d, t) {
      const BASE_URL = 'https://chat.hostedby.net';
      const scriptSrc = BASE_URL + '/packs/js/sdk.js';

      const isAlreadyCreated = !!document.querySelector(`[src='${scriptSrc}']`);

      if (isAlreadyCreated) return;
      const g = d.createElement(t) as any;
      const s = d.getElementsByTagName(t)[0] as any;
      g.src = scriptSrc;
      s.parentNode.insertBefore(g, s);
      g.async = !0;
      g.onload = function () {
        window.chatwootSDK!.run({
          websiteToken: chatwootToken,
          baseUrl: BASE_URL,
        });
      };
    })(document, 'script');
  }, [chatwootToken]);

  useEffect(() => {
    window.addEventListener(ChatwootSDKEvents.CHATWOOT_READY, onLoad);
    window.addEventListener(ChatwootSDKEvents.CHATWOOT_ERROR, onError);
    window.addEventListener(ChatwootSDKEvents.CHATWOOT_ON_MESSAGE, onMessage);

    const onOpenListener = EventBus.addListener(InternalChatwootEvents.OPEN_CHATWOOT, initializeWidget);

    return () => {
      window.removeEventListener(ChatwootSDKEvents.CHATWOOT_READY, onLoad);
      window.removeEventListener(ChatwootSDKEvents.CHATWOOT_ERROR, onError);
      window.removeEventListener(ChatwootSDKEvents.CHATWOOT_ON_MESSAGE, onMessage);
      onOpenListener.removeListener();
    };
  }, [initializeWidget, onError, onLoad, onMessage]);

  if (!chatwootToken || isMobile || isInvoicePage) {
    return null;
  }
  return (
    <div className='fixed bottom-[0.625rem] right-[0.625rem]' style={{ zIndex: Z_INDEX.chatwootWidget }}>
      <Button
        tooltip={translate('all.chatwoot.chatWithUs')}
        shape={ButtonShapes.circle}
        onClick={() => {
          setChatwootAttributes(attrs);
          initializeWidget();
        }}
        icon={(className, color) => {
          if (chatwootState === ChatwootState.LOADING) {
            return <LoaderIcon className={className} color={color} />;
          }
          return <ChatIcon className={className} color={color} />;
        }}
      />
    </div>
  );
};

export function setChatwootAttributes(attributes: Record<string, unknown>) {
  if (!window.$chatwoot) {
    return;
  }

  try {
    window.$chatwoot.setConversationCustomAttributes(attributes);
  } catch (error) {
    console.log(error);
  }
}
